import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { DocumentSigningStatusEnumType } from '__generated__/graphql';
import { FC } from 'react';

import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import ConfirmButton from 'primitives/ConfirmButton';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import documentSigningStatusToReadable from 'utils/enums/document-signing-status-to-readable';

const RETRY_CONTRIBUTION_AGREEMENT_MUTATION = gql(`
  mutation RetryContributionAgreement($investmentEntityId: ID!) {
    retryContributionAgreement(investmentEntityId: $investmentEntityId) {
      id
      status
      signingStatus
      url
    }
  }
`);

const ContributionAgreement: FC<{
  investmentEntityId: string;
  contributionAgreement?: {
    id: string;
    signingStatus: DocumentSigningStatusEnumType;
    url?: string | null;
  } | null;
  onRefresh: () => void;
}> = ({ investmentEntityId, contributionAgreement, onRefresh }) => {
  const [retryContributionAgreement, { loading, error }] = useMutation(
    RETRY_CONTRIBUTION_AGREEMENT_MUTATION
  );
  const { showToast, handleToastOpen, handleToastClose } = useToast();

  if (!contributionAgreement) return null;

  const { label, color } = documentSigningStatusToReadable(contributionAgreement.signingStatus);

  return (
    <div className="border-t border-gray-200 py-4">
      <h4 className="text-sm font-medium text-gray-900 mb-2">Contribution Agreement</h4>
      <Badge label={label} color={color} showIndicator />
      <div className="flex gap-2 mt-3">
        {contributionAgreement.url && (
          <Button onClick={() => window.open(contributionAgreement.url!, '_blank')}>
            View signed document
          </Button>
        )}
        <ConfirmButton
          onConfirm={async () => {
            await retryContributionAgreement({ variables: { investmentEntityId } });
            onRefresh();
            handleToastOpen();
          }}
          loading={loading}
          error={error}
          description="If this Contribution Agreement hasn't been sent yet, this will send it. If it's still pending signature, this will resend the signing reminder to the investor. If it's stuck (expired/rejected/errored), this will generate a fresh signing request."
        >
          Retry / Recheck Signing
        </ConfirmButton>
      </div>
      <Toast
        show={showToast}
        handleClose={handleToastClose}
        title="Contribution Agreement Updated"
        message="The signing request was sent, resent, or its status was refreshed."
      />
    </div>
  );
};

export default ContributionAgreement;
